import { LanguageCode, LanguageCodeLabels } from '@navio/nsdk';

/** Backend LanguageCode values used for localizable content (matches nsdk LanguageCode). */
export const LOCALIZATION_LANGUAGE_CODES = [
  'en-us',
  'nb',
  'sv',
  'de',
  'nl',
  'fr'
] as const satisfies readonly LanguageCode[];

export type LocalizationLanguageCode = (typeof LOCALIZATION_LANGUAGE_CODES)[number];

export const LOCALIZATION_LANGUAGE_LABELS: Record<LocalizationLanguageCode, string> = {
  'en-us': 'English',
  nb: 'Norwegian',
  sv: 'Swedish',
  de: 'German',
  nl: 'Dutch',
  fr: 'French'
};

/** Every backend language by code, with Bokmål shown to staff as plain "Norwegian". */
export const LANGUAGE_CODE_LABELS = Object.fromEntries(
  Object.entries(LanguageCode).map(([key, code]) => [
    code,
    key === 'NORWEGIAN_BOKMAL' ? 'Norwegian' : LanguageCodeLabels[key as keyof typeof LanguageCodeLabels]
  ])
) as Record<LanguageCode, string>;

export type LocalizationValues = Record<string, string>;

type LocalizationAuditUser = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  name?: string | null;
};

/** UI/editor row. `isSource` is client-only; create/patch use parent `sourceLanguageCode`. */
export type LocalizationRow<TValues extends LocalizationValues = LocalizationValues> = {
  languageCode: LocalizationLanguageCode | string;
  isSource: boolean;
  values: TValues;
  createdAt?: string | null;
  updatedAt?: string | null;
  createdBy?: LocalizationAuditUser | null;
  updatedBy?: LocalizationAuditUser | null;
};

export const isLocalizationLanguageCode = (value: string): value is LocalizationLanguageCode =>
  (LOCALIZATION_LANGUAGE_CODES as readonly string[]).includes(value);

export const emptyLocalizationValues = <TKey extends string>(fieldKeys: readonly TKey[]): Record<TKey, string> =>
  Object.fromEntries(fieldKeys.map((key) => [key, ''])) as Record<TKey, string>;

/**
 * Locales required for a department: primary language + always English.
 * Extra codes from `languages[]` can be passed via extraLanguageCodes.
 */
export const requiredLanguageCodesForDepartment = ({
  primaryLanguageCode,
  extraLanguageCodes = []
}: {
  primaryLanguageCode: string;
  extraLanguageCodes?: string[];
}): LocalizationLanguageCode[] => {
  const codes = new Set<LocalizationLanguageCode>(['en-us']);
  if (isLocalizationLanguageCode(primaryLanguageCode)) {
    codes.add(primaryLanguageCode);
  }
  for (const raw of extraLanguageCodes) {
    if (isLocalizationLanguageCode(raw)) {
      codes.add(raw);
    }
  }
  return LOCALIZATION_LANGUAGE_CODES.filter((code) => codes.has(code));
};

export const sourceLanguageFromDepartment = (primaryLanguageCode: string): LocalizationLanguageCode =>
  isLocalizationLanguageCode(primaryLanguageCode) ? primaryLanguageCode : 'en-us';

/** Merge required + extra codes, preserving LOCALIZATION_LANGUAGE_CODES order. */
export const mergeLocalizationLanguageCodes = (
  required: readonly string[],
  extra: readonly string[] = []
): LocalizationLanguageCode[] => {
  const codes = new Set<string>();
  for (const raw of [...required, ...extra]) {
    if (isLocalizationLanguageCode(raw)) {
      codes.add(raw);
    }
  }
  return LOCALIZATION_LANGUAGE_CODES.filter((code) => codes.has(code));
};

/** Customer app Lingui locales (`en` | `nb` | `se`) → backend LanguageCode. */
export const customerLocaleToLocalizationLanguage = (locale: string): LocalizationLanguageCode => {
  switch (locale) {
    case 'nb':
      return 'nb';
    case 'se':
    case 'sv':
      return 'sv';
    case 'de':
      return 'de';
    case 'nl':
      return 'nl';
    case 'fr':
      return 'fr';
    case 'en':
    case 'en-us':
    default:
      return 'en-us';
  }
};

export const createEmptyLocalizationRows = <TValues extends LocalizationValues>({
  languageCodes,
  sourceLanguageCode,
  emptyValues
}: {
  languageCodes: readonly string[];
  sourceLanguageCode: string;
  emptyValues: TValues;
}): LocalizationRow<TValues>[] => {
  const codes = languageCodes.includes(sourceLanguageCode) ? languageCodes : [...languageCodes, sourceLanguageCode];
  return codes.map((languageCode) => ({
    languageCode,
    isSource: languageCode === sourceLanguageCode,
    values: { ...emptyValues }
  }));
};

export const seedLocalizationsFromValues = <TValues extends LocalizationValues>({
  languageCodes,
  sourceLanguageCode,
  values
}: {
  languageCodes: readonly string[];
  sourceLanguageCode: string;
  values: TValues;
}): LocalizationRow<TValues>[] => {
  const codes = languageCodes.includes(sourceLanguageCode) ? languageCodes : [...languageCodes, sourceLanguageCode];
  return codes.map((languageCode) => ({
    languageCode,
    isSource: languageCode === sourceLanguageCode,
    values: { ...values }
  }));
};

/** Keep existing rows when possible; resize set to required languages. */
export const reconcileLocalizationRows = <TValues extends LocalizationValues>({
  existing,
  languageCodes,
  sourceLanguageCode,
  emptyValues,
  fieldKeys
}: {
  existing: LocalizationRow<TValues>[];
  languageCodes: readonly string[];
  sourceLanguageCode: string;
  emptyValues: TValues;
  fieldKeys: readonly (keyof TValues & string)[];
}): LocalizationRow<TValues>[] => {
  const byLang = new Map(existing.map((row) => [row.languageCode, row]));
  const codes = languageCodes.includes(sourceLanguageCode) ? languageCodes : [...languageCodes, sourceLanguageCode];
  return codes.map((languageCode) => {
    const prev = byLang.get(languageCode);
    if (prev) {
      const values = { ...emptyValues };
      for (const key of fieldKeys) {
        values[key] = (prev.values[key] ?? '') as TValues[typeof key];
      }
      return {
        ...prev,
        isSource: languageCode === sourceLanguageCode,
        values
      };
    }
    return {
      languageCode,
      isSource: languageCode === sourceLanguageCode,
      values: { ...emptyValues }
    };
  });
};

export const localizationsHaveRequiredFields = <TValues extends LocalizationValues>(
  localizations: LocalizationRow<TValues>[],
  requiredKeys: readonly (keyof TValues & string)[]
): boolean =>
  localizations.every((row) => requiredKeys.every((key) => String(row.values[key] ?? '').trim().length > 0));

/**
 * Build create/patch localization rows (nsdk `LocalizationRecordInput`).
 * Source language is set via parent `sourceLanguageCode`, not per-row `isSource`.
 */
export const toLocalizationsPayload = <TValues extends LocalizationValues>(
  localizations: LocalizationRow<TValues>[],
  fieldKeys: readonly (keyof TValues & string)[]
) =>
  localizations.map((row) => {
    const values = {} as TValues;
    for (const key of fieldKeys) {
      values[key] = String(row.values[key] ?? '').trim() as TValues[typeof key];
    }
    return {
      languageCode: row.languageCode,
      values
    };
  });
